import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import React, {Component, Fragment} from 'react';
import {connect} from 'react-redux';
import compose from 'recompose/compose';

import {reduxForm, SubmissionError} from 'redux-form';
import {updateSettings} from '../../reducers/auth';
import {openSnackbar} from '../../reducers/snackbar';

import Container from '@material-ui/core/Container';
import ErrorBoundary from '../../components/ErrorBoundary';
import Snackbar from '../../components/Snackbar/Snackbar';
import Tabs from '../../components/Tabs';

import {Dashboard as DashboardLayout} from '../../layouts';
import {confirmKeepChanges} from '../../libs/hooks';
import sendSegment from '../../libs/segment';
const DisabledPage = dynamic(() => import('./components/Disabled'));
const SettingsPage = dynamic(() => import('./components/Settings'));
const GuestPortalScreens = dynamic(() => import('./components/Screens'));
const AddonsPage = dynamic(() => import('./components/Addons'));
const IPWhitelist = dynamic(() => import('./components/IPWhitelist'));
const Templates = dynamic(() => import('./components/Templates'));
const BookingSources = dynamic(() => import('./components/BookingSources'));
const Preconfirmed = dynamic(() => import('./components/Preconfirmed'));

class GuestPortal extends Component {
    state = {
        tab: this.props.router.query.tab || 'screens',
    };

    sendSegment = (event, options) => {
        sendSegment(event, options);
    };

    componentWillReceiveProps(nextProps, nextContext) {
        const {user} = this.props;
        if (!user.id && nextProps.user && nextProps.user.settings) {
            this.autofillFields(nextProps.user);
        }
    }

    componentDidMount() {
        const {user} = this.props;
        if (user && user.id && user.settings) {
            this.autofillFields(user);
        }
    }

    autofillFields = (user) => {
        const {initialize} = this.props;
        const settings = user.settings || {};

        const defaultScreens = {
            Start: {red: true, orange: true, yellow: true, green: true},
            PersonalInfo: {red: true, orange: true, yellow: true, green: true},
            Coronavirus: {red: false, orange: false, yellow: false, green: false},
            TimeInfoEdit: {red: false, orange: false, yellow: false, green: false},
            GuestList: {red: true, orange: true, yellow: true, green: true},
            HouseRules: {red: true, orange: true, yellow: true, green: true},
            UsageAgreement: {red: true, orange: true, yellow: true, green: true},
            IDVerification: {red: true, orange: true, yellow: true, green: true},
            FileUpload: {red: false, orange: false, yellow: false, green: false},
            Selfie: {red: false, orange: false, yellow: false, green: false},
            PurposeOfStay: {red: true, orange: true, yellow: true, green: false},
            SecurityDeposit: {red: true, orange: true, yellow: false, green: false},
            AboutYou: {red: true, orange: true, yellow: true, green: false},
            BackgroundCheck: {red: false, orange: false, yellow: false, green: false},
            CreditCheck: {red: false, orange: false, yellow: false, green: false},
            LongTerm: {red: false, orange: false, yellow: false, green: false},
            CustomScreen: {red: false, orange: false, yellow: false, green: false},
            Overview: {red: true, orange: true, yellow: true, green: true},
            Finish: {red: true, orange: true, yellow: true, green: true},
        };

        settings.guest_portal_screens = {
            ...defaultScreens,
            ...(settings.guest_portal_screens || {}),
        };

        initialize({settings});
    };

    handleSubmit = async (form) => {
        const {openSnackbar, autofill, updateSettings, user} = this.props;
        const hasGuesty = Boolean(user.guesty && user.guesty.account_id);
        const settings = user.settings || {};

        // create settings object to update
        const data = {
            settings: {
                ...user.settings,
                ...form.settings,
                enable_guest_portal_guesty_hook: !!form.settings.enable_guest_portal_guesty_hook,
                guest_portal_guesty_hook_id: settings.guest_portal_guesty_hook_id || null,
            },
        };

        // save settings
        let res;
        try {
            res = await updateSettings(data);
        } catch (e) {
            openSnackbar(typeof e === 'string' ? e : 'Failed to update settings', null, {variant: 'error'});
            throw new SubmissionError({_error: e});
        }

        // update form with new settings
        const updatedSettings = res.settings || {};
        autofill('settings', updatedSettings);

        // show confirmation message to user
        openSnackbar('Settings updated');

        // log event with Segment
        this.sendSegment('track', {
            name: 'Updated Guest Portal Settings',
            data: {
                name: user.name,
                email: user.email,
                ...data,
            },
        });
    };

    handleTab = (event, tab) => {
        // addons saves progress as soon as a user hits a toggle
        // so we do not need to check if user wants to keep their changes
        if (this.state.tab !== 'addons' && !this.props.pristine && !confirmKeepChanges()) return;
        const {user} = this.props;
        this.setState({tab}, () => {
            this.autofillFields(user);
        });
    };

    renderTab = (tab) => {
        const {user, router, initialize, openSnackbar} = this.props;
        const props = {
            onSubmit: this.handleSubmit,
            user,
            openSnackbar,
        };
        let element;
        if (tab === 'settings') {
            router.replace(`/guestportal?tab=settings`);
            element = <SettingsPage {...props} />;
        }
        if (tab === 'screens') {
            router.replace(`/guestportal?tab=screens`);
            element = <GuestPortalScreens {...props} />;
        }
        if (tab === 'addons') {
            router.replace(`/guestportal?tab=addons`);
            element = <AddonsPage {...props} />;
        }
        if (tab === 'whitelist') {
            router.replace(`/guestportal?tab=whitelist`);
            element = <IPWhitelist {...props} />;
        }
        if (tab === 'templates') {
            router.replace(`/guestportal?tab=templates`);
            element = <Templates {...props} />;
        }
        if (tab === 'sources') {
            router.replace(`/guestportal?tab=sources`);
            element = <BookingSources {...props} />;
        }
        if (tab === 'preconfirmed') {
            router.replace(`/guestportal?tab=preconfirmed`);
            element = <Preconfirmed {...props} />;
        }
        if (element) return <ErrorBoundary>{element}</ErrorBoundary>;
    };

    render() {
        const {user} = this.props;
        const {tab} = this.state;
        const settings = user.settings || {};

        const tabs = [
            {label: 'Screens', value: 'screens'},
            {label: 'Settings', value: 'settings'},
            {label: 'Whitelist', value: 'whitelist'},
            {label: 'Addons', value: 'addons'},
            {label: 'Messages', value: 'templates'},
            {label: 'Sources', value: 'sources'},
            {label: 'Pre-Confirmed', value: 'preconfirmed'},
        ];

        return (
            <DashboardLayout title="Guest Portal">
                <Container maxWidth="md">
                    {settings.enable_guest_portal && (
                        <Fragment>
                            <Tabs onClick={this.handleTab} tab={tab} tabs={tabs} />

                            {this.renderTab(tab)}
                        </Fragment>
                    )}

                    {!settings.enable_guest_portal && <DisabledPage onSubmit={this.handleSubmit} />}
                </Container>
                <Snackbar />
            </DashboardLayout>
        );
    }
}

GuestPortal.propTypes = {
    // connected
    updateSettings: PropTypes.func.isRequired,
    openSnackbar: PropTypes.func.isRequired,
    user: PropTypes.object.isRequired,

    // redux-form
    initialize: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => {
    return {
        user: state.auth.user,
    };
};

const ReduxForm = {
    form: 'guestportal',
    destroyOnUnmount: false,
    forceUnregisterOnUnmount: true,
};

export {GuestPortal};
export default compose(
    connect(mapStateToProps, {
        updateSettings,
        openSnackbar,
    }),
    reduxForm(ReduxForm),
)(GuestPortal);
